import { ConfigResponse } from '../config/config.types'
import { IframeMsgType, SDK_INIT } from './messenger.messages'

/**
 * Utility class to send messages to the app's iframe.
 *
 * It's important that we listen for a handshake event (DONATION_READY_MSG_FROM_APP) before
 * we send events across. The iframe can be loading and the application still mounting otherwise.
 */
export default class MessengerIframe {
  private iframe?: HTMLIFrameElement
  private isAppReady = false
  private eventQueue: IframeMsgType[] = []

  /**
   * Registers the iframe and marks it as available to receive messages.
   *
   * @param iframe - The iframe element where messages will be sent
   */
  public register = (iframe: HTMLIFrameElement): void => {
    this.iframe = iframe

    /**
     * If the app is immediately ready, flush the queue.
     */
    if (this.isAppReady) {
      this._flushQueue()
    }
  }

  /**
   * Resets the iframe messenger by clearing the iframe reference and resetting state.
   * Should be called when the iframe is closed.
   */
  public reset = (): void => {
    this.iframe = undefined
    this.isAppReady = false
    this.eventQueue = []
  }

  /**
   * Sends a message to the iframe immediately if the app is ready, otherwise queues it.
   *
   * @param message - The message to send to the iframe
   */
  public sendMessage = (message: IframeMsgType): void => {
    if (this.isAppReady && this.iframe?.contentWindow) {
      this._postMessage(message)
    } else {
      this._queueMessage(message)
    }
  }

  /**
   * Queues a message if the iframe is not ready to receive it.
   *
   * @param message - The message to queue until the app is ready
   */
  private _queueMessage = (message: IframeMsgType) => {
    this.eventQueue.push(message)
  }

  /**
   * Flushes the queue and sends all queued messages once the app is ready.
   */
  private _flushQueue = (): void => {
    if (this.isAppReady && this.iframe?.contentWindow) {
      while (this.eventQueue.length > 0) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const message = this.eventQueue.shift()!

        this._postMessage(message)
      }
    }
  }

  /**
   * Sends a message to the iframe's content window.
   *
   * @param message - The message to send to the iframe
   */
  private _postMessage = (message: IframeMsgType) => {
    if (this.iframe?.contentWindow) {
      this.iframe.contentWindow.postMessage(message, '*')
    }
  }

  public setAppReady = (): void => {
    this.isAppReady = true
  }

  public sendSDKInitMessage = (config: ConfigResponse, hostUrl?: string): void => {
    const safeguardToken = process.env.SAFEGUARD_TOKEN

    // Send the initialization message once the app is ready
    this._postMessage({
      type: SDK_INIT,
      payload: {
        safeguardToken,
        iaa: config.iaa,
        hostUrl,
      },
    })

    // Flush any messages that were queued while waiting for the app to be ready
    this._flushQueue()
  }
}
